import { GovernanceType } from 'Models/Governance';

import { ComplianceRequirementStatusValues, Regulation } from './ComplianceRequirements';
import { FileUpdates, UploadedFile } from './Files';
import { Effectiveness } from './OperationalControls';

/** Represents an existing AI Governance once it has been created. */
export interface AiGovernance {
    created_by: string;
    created_timestamp: string;
    description: string;
    id: string;
    title: string;
    type: GovernanceType;
    input_file?: UploadedFile;
    output_file?: UploadedFile; // Optional only because a database record is created before the output file is generated. Once the GenAI model has generated the output file, this attribute is set.
    regulation?: Regulation;
}

/** A request to create a new AI Governance. */
export interface AiGovernanceCreateRequest {
    description: string;
    title: string;
    type: GovernanceType;
    input_file_id?: string;
    input_filename?: string;
    regulation?: Regulation;
}

/**
 * Represents an AI TPRM Service Assessment entity.
 * @param created_by: The ID of the user who launched AI TPRM Service Assessment. Gets overwritten each time AI TPRM Service Assessment is launched.
 * @param created_timestamp: The timestamp when AI TPRM Service Assessment was launched. Gets overwritten each time AI TPRM Service Assessment is launched.
 * @param include_client_responses: Whether to include the Client's responses to the TPRM Service Assessment in the GenAI model prompt.
 * @param include_third_party_responses: Whether to include the Third Party's responses to the TPRM Service Assessment in the GenAI model prompt.
 * @param files: An optional set of assessment-wide files included in the GenAI model prompt for all Controls.
 */
export interface AiTprmServiceAssessment {
    created_by: string;
    created_timestamp: string;
    include_client_responses: boolean;
    include_third_party_responses: boolean;
    third_party_id: string;
    third_party_service_id: string;
    files: UploadedFile[];
}

/**
 * A request to start a multi step process utilizing artificial intelligence (AI) to provide recommendations for a TPRM Service assessment.
 * @param include_client_responses Whether to include the Client's responses to the TPRM Service Assessment in the GenAI model prompt.
 * @param include_third_party_responses Whether to include the Third Party's responses to the TPRM Service Assessment in the GenAI model prompt.
 * @param file_updates: Assessment-wide files to be included (or removed) from the GenAI model prompt for all Controls.
 */
export interface AiTprmServiceAssessmentRequest {
    include_client_responses: boolean;
    include_third_party_responses: boolean;
    file_updates: FileUpdates;
}

export interface AiOperationalControlAssessmentControlAnalysis {
    control_effectiveness_rating: Effectiveness;
    control_identifier: string;
    summary: string;
    improvements: string[];
    key_observations: string[];
}

export interface AiOperationalControlAssessmentComplianceRequirementAnalysis {
    regulation: string;
    identifier: string;
    status: ComplianceRequirementStatusValues.MET | ComplianceRequirementStatusValues.NOT_MET;
    explanation: string;
    suggestions: string[];
    assumptions?: string;
}

export interface AiOperationalControlAssessmentFinalResult {
    control_analysis: AiOperationalControlAssessmentControlAnalysis;
    requirement_analyses: AiOperationalControlAssessmentComplianceRequirementAnalysis[];
    timestamp: string;
}

export enum AiOperationalControlAssessmentGenerationStatus {
    IN_PROGRESS = 'In progress',
    SUCCESS = 'Success',
    FAILURE = 'Failure',
}

export interface AiOperationalControlAssessment {
    final_result?: AiOperationalControlAssessmentFinalResult;
    generation_status?: AiOperationalControlAssessmentGenerationStatus;
}
